import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import smoothScrollState from './smoothScrollState';

const useGSAPScrollSmooth = (currentPage, pathname) => {
  const scrollContainerRef = useRef(null);
  const scrollContentRef = useRef(null);
  const smoothScrollRef = useRef(null);
  const scrollDataRef = useRef({
    current: 0,
    target: 0,
    ease: 0.08,
    isScrolling: false,
    lastTime: 0
  });

  useEffect(() => {
    if (!scrollContainerRef.current || !scrollContentRef.current) return;

    /*
     * Land at the top with NOTHING left to animate.
     *
     * Order matters here. `window.scrollTo(0, 0)` alone is not enough: the
     * eased loop below drives the content off `scrollData.current`, which still
     * holds the previous page's offset, so it would ease from there down to 0
     * over ~1.9s — a slow slide that outlasts the detail transition's cover
     * (which lifts 0.55s after navigating) and shows up as a blip just as the
     * new page is revealed.
     *
     * So the eased state is zeroed and the transform written synchronously, in
     * the same frame as the scroll reset. There is then no difference for the
     * loop to interpolate and the page is simply already at the top when the
     * cover comes off.
     *
     * The body height is cleared too: it still describes the OUTGOING page's
     * length, and leaving it means the browser can clamp the scroll position
     * against a stale range mid-reset. `updateHeight()` sets the real value a
     * few lines below, once the new content is measurable.
     */
    document.body.style.height = '';
    window.scrollTo(0, 0);
    scrollDataRef.current.current = 0;
    scrollDataRef.current.target = 0;
    scrollDataRef.current.isScrolling = false;
    smoothScrollState.offset = 0;
    gsap.set(scrollContentRef.current, {
      transform: 'translate3d(0, 0, 0)',
      force3D: true,
    });

    // Check if device is mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                     window.innerWidth <= 768;

    // If mobile, disable smooth scroll and return early
    if (isMobile) {
      // Native scrolling on mobile — rails should use plain measurements.
      smoothScrollState.enabled = false;
      smoothScrollState.offset = 0;

      const container = scrollContainerRef.current;
      const content = scrollContentRef.current;

      // Reset to normal scroll behavior for mobile
      gsap.set(container, {
        position: 'static',
        height: 'auto',
        overflow: 'visible'
      });

      gsap.set(content, {
        transform: 'none'
      });

      document.body.style.height = '';
      return;
    }

    const container = scrollContainerRef.current;
    const content = scrollContentRef.current;
    const scrollData = scrollDataRef.current;

    // Reset scroll position for smooth scroll
    scrollData.current = 0;
    scrollData.target = 0;

    // Publish that smooth scroll is driving, so follow-sticky rails share the
    // same eased offset instead of snapping to raw scroll.
    smoothScrollState.enabled = true;
    smoothScrollState.offset = 0;

    // Set up scroll container
    gsap.set(container, {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100vh',
      overflow: 'hidden'
    });

    // Reset content position when page changes
    gsap.set(content, {
      transform: 'translate3d(0, 0, 0)',
      force3D: true
    });

    // Calculate content height and set body height. The native scrollbar's
    // range is driven by body height, so it must track the real content height
    // — otherwise the page can't scroll all the way to the bottom.
    const updateHeight = () => {
      const contentHeight = content.scrollHeight;
      document.body.style.height = `${contentHeight}px`;
    };

    // Measure now, and again shortly after in case layout is still settling.
    updateHeight();
    const initialTimeout = setTimeout(updateHeight, 100);

    // Re-measure whenever the content actually changes size. This is the fix
    // for the "can't scroll to the bottom until I resize" bug: async Firestore
    // data and late-loading images grow the content after the initial measure,
    // and a fixed one-shot measurement misses that. ResizeObserver catches
    // every reflow.
    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updateHeight());
      resizeObserver.observe(content);
    }

    // Images can load after render and reflow the layout; re-measure on each.
    const onImageLoad = () => updateHeight();
    content.addEventListener('load', onImageLoad, true); // capture: catches <img> load

    // Optimized smooth scroll with throttling
    const smoothScroll = (timestamp) => {
      // Completely disable smooth scroll when carousel is active to prevent conflicts
      if (document.documentElement.classList.contains('carousel-active')) {
        smoothScrollRef.current = requestAnimationFrame(smoothScroll);
        return;
      }
      
      const deltaTime = timestamp - scrollData.lastTime;
      
      // Throttle to ~60fps
      if (deltaTime < 16.67) {
        smoothScrollRef.current = requestAnimationFrame(smoothScroll);
        return;
      }
      
      scrollData.lastTime = timestamp;
      scrollData.target = window.pageYOffset;
      
      // Calculate difference
      const diff = scrollData.target - scrollData.current;
      
      // Only update if there's a meaningful difference
      if (Math.abs(diff) > 0.1) {
        scrollData.current += diff * scrollData.ease;

        // Use transform3d for hardware acceleration
        gsap.set(content, {
          transform: `translate3d(0, ${-scrollData.current}px, 0)`,
          force3D: true
        });

        // Share the eased offset so rails move in lockstep with the content.
        smoothScrollState.offset = scrollData.current;

        scrollData.isScrolling = true;
      } else {
        // Snap to target when close enough
        if (scrollData.isScrolling) {
          scrollData.current = scrollData.target;
          gsap.set(content, {
            transform: `translate3d(0, ${-scrollData.current}px, 0)`,
            force3D: true
          });
          smoothScrollState.offset = scrollData.current;
          scrollData.isScrolling = false;
        }
      }

      smoothScrollRef.current = requestAnimationFrame(smoothScroll);
    };

    smoothScrollRef.current = requestAnimationFrame(smoothScroll);

    // Handle resize with debouncing
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateHeight, 150);
    };

    window.addEventListener('resize', handleResize, { passive: true });

    // Cleanup
    return () => {
      if (smoothScrollRef.current) {
        cancelAnimationFrame(smoothScrollRef.current);
      }
      clearTimeout(resizeTimeout);
      clearTimeout(initialTimeout);
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) resizeObserver.disconnect();
      content.removeEventListener('load', onImageLoad, true);
      document.body.style.height = '';
      smoothScrollState.enabled = false;
      smoothScrollState.offset = 0;
    };
    /*
     * Keyed on the full pathname, not just the section.
     *
     * `currentPage` is only the first path segment, so moving from `/web` to
     * `/web/campuslm` left it unchanged and this whole effect never re-ran —
     * the new page inherited the old one's scroll offset, body height and
     * pending ease. That inheritance was the blip. A detail page is a different
     * document as far as scrolling is concerned, so it gets a fresh setup.
     */
  }, [currentPage, pathname]);

  return { scrollContainerRef, scrollContentRef };
};

export default useGSAPScrollSmooth;